import React, { useState, useContext, useEffect } from "react";
import { HashLoader } from "react-spinners";
import {
  FaFileUpload,
  FaTrash,
  FaArrowLeft,
  FaArrowRight,
  FaInfoCircle,
} from "react-icons/fa";
import { GoogleAnalyticsContext } from "../../../Hooks/GoogleAnalyticsContext";
import { useAuthenticatedFetch } from "../../../Hooks/useFetch";
import config from "../../../config";
import toast from "react-hot-toast";
import DocumentUploader from "../DocumentUploader/DocumentUploader";

// Define the result type for the automarker - updated to match new structure
interface QuestionResult {
  questionNumber: string;
  question: string;
  studentAnswer: string;
  score: string;
  isCorrect: string;
  explanation: string;
}

// Marking type options
const markingTypeOptions = [
  { value: "full", label: "Full (only full points or zero)" },
  { value: "partial", label: "Partial (award partial credit)" },
  { value: "custom", label: "Custom (follow provided rubric)" },
];

// Step definition
interface Step {
  id: string;
  title: string;
  description: string;
}

const steps: Step[] = [
  {
    id: "quiz",
    title: "Quiz Content",
    description:
      "Enter the quiz content including questions and student answers",
  },
  {
    id: "correctAnswers",
    title: "Correct Answers",
    description: "Enter the correct answers for each question (optional)",
  },
  {
    id: "markingType",
    title: "Marking Type",
    description: "Select how you want the quiz to be marked",
  },
  {
    id: "customRubric",
    title: "Custom Rubric",
    description:
      "Enter a custom rubric to follow (required if using custom marking type)",
  },
  {
    id: "review",
    title: "Review",
    description: "Review your inputs before generating",
  },
];

// Tooltip component for examples
const InfoTooltip: React.FC<{ title: string; content: React.ReactNode }> = ({
  title,
  content,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<"left" | "right">("right");
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Check position when tooltip becomes visible
  useEffect(() => {
    if (isVisible && buttonRef.current && tooltipRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipWidth = tooltipRef.current.offsetWidth;
      const viewportWidth = window.innerWidth;

      // If tooltip would go off the right edge, position it to the left instead
      if (buttonRect.left + tooltipWidth > viewportWidth) {
        setPosition("left");
      } else {
        setPosition("right");
      }
    }
  }, [isVisible]);

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        className="flex items-center text-blue-600 hover:text-blue-800"
        onClick={() => setIsVisible(!isVisible)}
        onBlur={() => setTimeout(() => setIsVisible(false), 200)}
      >
        <FaInfoCircle className="mr-1" />
        <span className="text-sm">Example</span>
      </button>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute top-full mt-2 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50 ${
            position === "right" ? "left-0" : "right-0"
          }`}
        >
          <div className="p-3">
            <h4 className="font-medium text-gray-800 mb-2">{title}</h4>
            <div className="text-sm text-gray-600">{content}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const AIAutomarker: React.FC = () => {
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const { res, callAPI } = useAuthenticatedFetch({
    url: `${config.API_URI}/api/prompt/17/execute`, // Updated prompt ID to 16
    method: "POST",
  });

  // State for inputs
  const [quizContent, setQuizContent] = useState<string>("");
  const [correctAnswers, setCorrectAnswers] = useState<string>("");
  const [markingType, setMarkingType] = useState<string>("full");
  const [customRubric, setCustomRubric] = useState<string>("");

  // State for UI
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<QuestionResult[] | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  // Validate current step
  const validateCurrentStep = (): boolean => {
    const step = steps[currentStep];

    switch (step.id) {
      case "quiz":
        if (!quizContent) {
          toast.error("Quiz content is required");
          return false;
        }
        break;
      case "correctAnswers":
        // No validation needed as it's optional
        break;
      case "customRubric":
        if (markingType === "custom" && !customRubric) {
          toast.error(
            "Custom rubric is required when using custom marking type"
          );
          return false;
        }
        break;
      default:
        break;
    }

    return true;
  };

  // Navigate to next step
  const goToNextStep = () => {
    if (validateCurrentStep()) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
        logEvent("AIAutomarker", "NextStep", steps[currentStep + 1].id);
      } else {
        handleGenerate();
      }
    }
  };

  // Navigate to previous step
  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      logEvent("AIAutomarker", "PreviousStep", steps[currentStep - 1].id);
    }
  };

  // Handle generate
  const handleGenerate = async () => {
    if (!validateInputs()) return;

    setIsGenerating(true);

    try {
      // Log the generate event
      logEvent("AIAutomarker", "Generate", markingType);

      // Prepare the payload
      const payload = {
        inputs: {
          quizContent,
          correctAnswers: correctAnswers || undefined,
          markingType,
          customRubric: markingType === "custom" ? customRubric : undefined,
        },
      };

      // Call the API
      await callAPI(JSON.stringify(payload));
    } catch (error) {
      console.error("Error marking quiz:", error);
      toast.error("Failed to mark quiz");
      setIsGenerating(false);
    }
  };

  // Handle API response
  useEffect(() => {
    if (res.statusCode === 200 && res.data) {
      setResults(res.data);
      console.log(res.data);
      toast.success("Quiz marked successfully!");
      setIsGenerating(false);
    } else if (res.error) {
      toast.error("Failed to mark quiz");
      setIsGenerating(false);
    }
  }, [res]);

  // Validate all inputs
  const validateInputs = (): boolean => {
    if (!quizContent) {
      toast.error("Quiz content is required");
      return false;
    }

    // Correct answers are optional, so no validation needed

    if (markingType === "custom" && !customRubric) {
      toast.error("Custom rubric is required when using custom marking type");
      return false;
    }

    return true;
  };

  // Reset the form
  const resetForm = () => {
    setQuizContent("");
    setCorrectAnswers("");
    setMarkingType("full");
    setCustomRubric("");
    setResults(null);
    setCurrentStep(0);
    logEvent("AIAutomarker", "Reset", "Reset form");
  };

  // Calculate total score from question results
  const calculateTotalScore = (): number => {
    if (!results || results.length === 0) {
      return 0;
    }

    const totalScore = results.reduce(
      (sum, result) => sum + parseFloat(result.score),
      0
    );
    return totalScore / results.length;
  };

  // Handle content extracted from document uploader
  const handleQuizContentExtracted = (content: string) => {
    setQuizContent(content);
  };

  const handleCorrectAnswersExtracted = (content: string) => {
    setCorrectAnswers(content);
  };

  const handleCustomRubricExtracted = (content: string) => {
    setCustomRubric(content);
  };

  // Render the results
  const renderResults = () => {
    if (!results) return null;

    const totalScore = calculateTotalScore();

    return (
      <div className="mt-8">
        <h2 className="text-2xl font-bold mb-4">Results</h2>
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold">Total Score</h3>
            <div
              className={`text-3xl font-bold ${
                totalScore >= 0.8
                  ? "text-green-600"
                  : totalScore >= 0.5
                  ? "text-yellow-600"
                  : "text-red-600"
              }`}
            >
              {Math.round(totalScore * 100)}%
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {results && results.length > 0 ? (
            results.map((result) => (
              <div
                key={result.questionNumber}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">
                    Question {result.questionNumber}
                  </h3>
                  <div
                    className={`text-xl font-bold ${
                      parseFloat(result.score) >= 0.8
                        ? "text-green-600"
                        : parseFloat(result.score) >= 0.5
                        ? "text-yellow-600"
                        : "text-red-600"
                    }`}
                  >
                    {Math.round(parseFloat(result.score) * 100)}%
                  </div>
                </div>

                <div className="mb-3">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      result.isCorrect === "true"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {result.isCorrect === "true" ? "Correct" : "Incorrect"}
                  </span>
                </div>

                <div className="mt-4 space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">
                      Question:
                    </h4>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {result.question}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">
                      Student's Answer:
                    </h4>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {result.studentAnswer}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">
                      Explanation:
                    </h4>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-md">
                      {result.explanation}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-gray-600">No question results available.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render the current step
  const renderCurrentStep = () => {
    const step = steps[currentStep];

    switch (step.id) {
      case "quiz":
        return (
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-4">
                <DocumentUploader
                  onContentExtracted={handleQuizContentExtracted}
                  isLoading={isGenerating}
                />
              </div>
              <InfoTooltip
                title="Quiz Content Format"
                content={
                  <div>
                    <p className="mb-2">
                      Format your quiz content with clear question numbering and
                      student answers:
                    </p>
                    <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
                      {`Q1: What is the capital of France?
Student Answer: Paris

Q2: Solve the equation: 2x + 5 = 13
Student Answer: x = 4

Q3: Explain the process of photosynthesis.
Student Answer: Photosynthesis is the process by which plants convert sunlight into energy...`}
                    </pre>
                  </div>
                }
              />
            </div>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              placeholder="Enter quiz content here..."
              value={quizContent}
              onChange={(e) => setQuizContent(e.target.value)}
              rows={8}
            />
          </div>
        );

      case "correctAnswers":
        return (
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-4">
                <DocumentUploader
                  onContentExtracted={handleCorrectAnswersExtracted}
                  isLoading={isGenerating}
                />
              </div>
              <InfoTooltip
                title="Correct Answers Format"
                content={
                  <div>
                    <p className="mb-2">
                      Format your correct answers with matching question
                      numbers:
                    </p>
                    <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
                      {`Q1: Paris

Q2: x = 4

Q3: Photosynthesis is the process by which plants convert sunlight into energy through a series of chemical reactions. The process involves capturing light energy, converting it to chemical energy, and using it to synthesize organic compounds from carbon dioxide and water.`}
                    </pre>
                  </div>
                }
              />
            </div>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              placeholder="Enter correct answers here..."
              value={correctAnswers}
              onChange={(e) => setCorrectAnswers(e.target.value)}
              rows={8}
            />
          </div>
        );

      case "markingType":
        return (
          <div className="mb-6">
            <div className="flex justify-end mb-4">
              <InfoTooltip
                title="Marking Types Explained"
                content={
                  <div>
                    <p className="mb-2">
                      Choose the marking style that best fits your assessment
                      needs:
                    </p>
                    <ul className="list-disc pl-4 space-y-1 text-xs">
                      <li>
                        <strong>Full:</strong> Students receive either full
                        points or zero for each question
                      </li>
                      <li>
                        <strong>Partial:</strong> Students can receive partial
                        credit for partially correct answers
                      </li>
                      <li>
                        <strong>Custom:</strong> Follow a specific rubric you
                        provide for more nuanced grading
                      </li>
                    </ul>
                  </div>
                }
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {markingTypeOptions.map((option) => (
                <div
                  key={option.value}
                  className={`p-4 border rounded-md cursor-pointer transition-colors ${
                    markingType === option.value
                      ? "border-yellow-500 bg-yellow-50"
                      : "border-gray-300 hover:border-yellow-300"
                  }`}
                  onClick={() => setMarkingType(option.value)}
                >
                  <div className="font-medium">{option.label}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case "customRubric":
        return (
          <div className="flex flex-col">
            {markingType === "custom" ? (
              <>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex space-x-4">
                    <DocumentUploader
                      onContentExtracted={handleCustomRubricExtracted}
                      isLoading={isGenerating}
                    />
                  </div>
                  <InfoTooltip
                    title="Custom Rubric Format"
                    content={
                      <div>
                        <p className="mb-2">
                          Format your custom rubric with clear scoring criteria:
                        </p>
                        <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
                          {`Q1: Capital of France (1 point)
- Correct answer (Paris): 1 point
- Partially correct (mentions France but wrong city): 0.5 points
- Incorrect: 0 points

Q2: Math equation (2 points)
- Correct answer (x = 4): 2 points
- Correct setup but wrong calculation: 1 point
- Incorrect: 0 points

Q3: Photosynthesis explanation (3 points)
- Complete and accurate explanation: 3 points
- Mostly correct with minor errors: 2 points
- Partially correct with significant gaps: 1 point
- Incorrect or minimal response: 0 points`}
                        </pre>
                      </div>
                    }
                  />
                </div>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="Enter custom rubric here..."
                  value={customRubric}
                  onChange={(e) => setCustomRubric(e.target.value)}
                  rows={8}
                />
              </>
            ) : (
              <div className="p-4 bg-gray-50 rounded-md text-gray-500">
                Custom rubric is only required when using the "Custom" marking
                type.
              </div>
            )}
          </div>
        );

      case "review":
        return (
          <div className="space-y-6">
            <div className="bg-white p-4 rounded-md border border-gray-200">
              <h3 className="font-medium text-gray-700 mb-2">Quiz Content</h3>
              <p className="text-gray-600 text-sm mb-2">
                {quizContent.length > 100
                  ? `${quizContent.substring(0, 100)}...`
                  : quizContent}
              </p>
              <p className="text-xs text-gray-500">
                {quizContent.length} characters
              </p>
            </div>

            <div className="bg-white p-4 rounded-md border border-gray-200">
              <h3 className="font-medium text-gray-700 mb-2">
                Correct Answers
              </h3>
              <p className="text-gray-600 text-sm mb-2">
                {correctAnswers.length > 100
                  ? `${correctAnswers.substring(0, 100)}...`
                  : correctAnswers}
              </p>
              <p className="text-xs text-gray-500">
                {correctAnswers.length} characters
              </p>
            </div>

            <div className="bg-white p-4 rounded-md border border-gray-200">
              <h3 className="font-medium text-gray-700 mb-2">Marking Type</h3>
              <p className="text-gray-600">
                {markingTypeOptions.find((opt) => opt.value === markingType)
                  ?.label || markingType}
              </p>
            </div>

            {markingType === "custom" && (
              <div className="bg-white p-4 rounded-md border border-gray-200">
                <h3 className="font-medium text-gray-700 mb-2">
                  Custom Rubric
                </h3>
                <p className="text-gray-600 text-sm mb-2">
                  {customRubric.length > 100
                    ? `${customRubric.substring(0, 100)}...`
                    : customRubric}
                </p>
                <p className="text-xs text-gray-500">
                  {customRubric.length} characters
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center mb-6">
        <h1 className="text-3xl font-bold">AI Quiz Automarker</h1>
        <span className="ml-2 px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
          Beta
        </span>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Welcome to the AI Quiz Automarker! This tool uses artificial
              intelligence to automatically grade quizzes and assessments.
              Simply input your quiz content, optionally provide correct
              answers, choose a marking type, and let our AI analyze student
              responses. You'll receive detailed feedback including scores,
              correctness indicators, and explanations for each answer.
            </p>
          </div>
        </div>
      </div>

      {!results ? (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex justify-between mb-2 relative">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex flex-col items-center relative ${
                    index === steps.length - 1 ? "w-auto" : "w-full"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 z-10 ${
                      index <= currentStep
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div className="text-xs text-center max-w-[80px]">
                    {step.title}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`h-1 w-full absolute top-4 left-0 ${
                        index < currentStep ? "bg-yellow-500" : "bg-gray-200"
                      }`}
                      style={{
                        width: `calc(100% - 1rem)`,
                        left: `calc(50% + 0.5rem)`,
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <h2 className="text-xl font-semibold mb-1">
            {steps[currentStep].title}
          </h2>
          <div className="mb-2">
            <span
              className={`px-2 py-0.5 text-xs rounded-full ${
                currentStep === 0 ||
                currentStep === 2 ||
                currentStep === 4 ||
                (currentStep === 3 && markingType === "custom")
                  ? "bg-red-100 text-red-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {currentStep === 0 ||
              currentStep === 2 ||
              currentStep === 4 ||
              (currentStep === 3 && markingType === "custom")
                ? "Required"
                : "Optional"}
            </span>
          </div>
          <p className="text-gray-600 mb-6">{steps[currentStep].description}</p>

          {renderCurrentStep()}

          <div className="mt-6 flex justify-between">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors flex items-center"
              onClick={goToPreviousStep}
              disabled={currentStep === 0}
            >
              <FaArrowLeft className="mr-2" />
              Previous
            </button>

            <div className="flex space-x-4">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                onClick={resetForm}
                disabled={isGenerating}
              >
                Reset
              </button>

              <button
                className="px-4 py-2 bg-yellow-400 text-gray-800 rounded-md hover:bg-yellow-500 transition-colors flex items-center"
                onClick={goToNextStep}
                disabled={isGenerating}
              >
                {currentStep === steps.length - 1 ? (
                  isGenerating ? (
                    <>
                      <HashLoader size={18} color="#4B5563" className="mr-2" />
                      <span>Marking Quiz...</span>
                    </>
                  ) : (
                    "Mark Quiz"
                  )
                ) : (
                  <>
                    Next
                    <FaArrowRight className="ml-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Quiz Marking Complete</h2>
              <button
                className="px-4 py-2 bg-yellow-400 text-gray-800 rounded-md hover:bg-yellow-500 transition-colors"
                onClick={resetForm}
              >
                Mark Another Quiz
              </button>
            </div>
          </div>
          {renderResults()}
        </>
      )}
    </div>
  );
};

export default AIAutomarker;
